/**
 * Geometric constraint mutations (DYN-3 Batch D).
 *
 * Mutations to add / update / remove geometric constraints in a block's
 * dynamicMetadata. Kept separate from `dynamicAuthoringMutations.ts` to
 * stay within the CLAUDE.md size budget and to isolate DYN-3-specific
 * logic.
 *
 * Public surface:
 *   - addGeometricConstraintMutation
 *   - updateGeometricConstraintMutation
 *   - removeGeometricConstraintMutation
 *
 * All mutations enforce:
 *   - the target block must exist in `document.blocks`;
 *   - arity is validated per-kind via `validateGeometricConstraint`
 *     against the block's entity id set;
 *   - id uniqueness within the block's geometricConstraints[] list.
 *
 * The mutations never touch the solver — they only record the constraint
 * declaratively. The solver (packages/block-evaluator/constraintSolver.ts)
 * consumes the ordered list at evaluation time.
 */

import type {
  BlockDefinition,
  DrawingDocument,
  DynamicBlockMetadata,
  GeometricConstraint,
} from "@dxf-ai-editor/drawing-model";
import {
  dynamicBlockMetadataSchema,
  validateDynamicMetadata,
  validateGeometricConstraint,
} from "@dxf-ai-editor/drawing-model";

export interface GeometricConstraintMutationResult {
  updatedDocument: DrawingDocument;
  summary: string;
}

// ═══════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════

function requireBlock(
  document: DrawingDocument,
  blockName: string,
): BlockDefinition {
  const blockDef = document.blocks[blockName];
  if (!blockDef) {
    throw new Error(`Block '${blockName}' not found`);
  }
  return blockDef;
}

function ensureMetadata(blockDef: BlockDefinition): DynamicBlockMetadata {
  if (blockDef.dynamicMetadata) return blockDef.dynamicMetadata;
  return dynamicBlockMetadataSchema.parse({});
}

function replaceBlock(
  document: DrawingDocument,
  blockName: string,
  update: BlockDefinition,
): DrawingDocument {
  return {
    ...document,
    blocks: { ...document.blocks, [blockName]: update },
  };
}

/**
 * Extract the set of entity ids from a block definition (its
 * `entities[]` array). Used as the `knownEntityIds` input to
 * validateGeometricConstraint so the validator can flag entity references
 * that don't exist in the block.
 */
function collectEntityIds(blockDef: BlockDefinition): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const ent of blockDef.entities ?? []) {
    if ((ent as { id?: string }).id) {
      ids.add((ent as { id: string }).id);
    }
  }
  return ids;
}

function validateAndCommit(
  document: DrawingDocument,
  blockDef: BlockDefinition,
  nextMeta: DynamicBlockMetadata,
  summary: string,
): GeometricConstraintMutationResult {
  const parsed = dynamicBlockMetadataSchema.parse(nextMeta);
  const knownEntityIds = collectEntityIds(blockDef);
  const errors = validateDynamicMetadata(parsed, knownEntityIds);
  if (errors.length > 0) {
    throw new Error(
      `Invalid geometric constraint state: ${errors.join("; ")}`,
    );
  }
  const nextBlockDef: BlockDefinition = {
    ...blockDef,
    dynamicMetadata: parsed,
  };
  return {
    updatedDocument: replaceBlock(document, blockDef.name, nextBlockDef),
    summary,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// ADD geometric constraint
// ═══════════════════════════════════════════════════════════════════════

export interface AddGeometricConstraintInput {
  document: DrawingDocument;
  blockName: string;
  constraint: GeometricConstraint;
}

export function addGeometricConstraintMutation(
  input: AddGeometricConstraintInput,
): GeometricConstraintMutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const existing = meta.geometricConstraints ?? [];
  if (existing.some((c) => c.id === input.constraint.id)) {
    throw new Error(
      `Geometric constraint id '${input.constraint.id}' already exists`,
    );
  }
  // Pre-validate the individual constraint so the error message pins the
  // problem to this specific record rather than the whole metadata.
  const knownEntityIds = collectEntityIds(blockDef);
  const errors = validateGeometricConstraint(input.constraint, knownEntityIds);
  if (errors.length > 0) {
    throw new Error(`Invalid geometric constraint: ${errors.join("; ")}`);
  }
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    geometricConstraints: [...existing, input.constraint],
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Added ${input.constraint.kind} geometric constraint '${input.constraint.id}' to block '${input.blockName}'`,
  );
}

// ═══════════════════════════════════════════════════════════════════════
// UPDATE geometric constraint
// ═══════════════════════════════════════════════════════════════════════

export interface UpdateGeometricConstraintInput {
  document: DrawingDocument;
  blockName: string;
  constraintId: string;
  updates: Partial<GeometricConstraint>;
}

export function updateGeometricConstraintMutation(
  input: UpdateGeometricConstraintInput,
): GeometricConstraintMutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const existing = (meta.geometricConstraints ?? []).find(
    (c) => c.id === input.constraintId,
  );
  if (!existing) {
    throw new Error(`Geometric constraint '${input.constraintId}' not found`);
  }
  // Keep the discriminator (kind) fixed — changing the kind = remove + add.
  const merged: GeometricConstraint = {
    ...existing,
    ...input.updates,
    kind: existing.kind,
    id: existing.id,
  };
  const knownEntityIds = collectEntityIds(blockDef);
  const errors = validateGeometricConstraint(merged, knownEntityIds);
  if (errors.length > 0) {
    throw new Error(`Invalid geometric constraint: ${errors.join("; ")}`);
  }
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    geometricConstraints: (meta.geometricConstraints ?? []).map((c) =>
      c.id === input.constraintId ? merged : c,
    ),
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Updated geometric constraint '${input.constraintId}' in block '${input.blockName}'`,
  );
}

// ═══════════════════════════════════════════════════════════════════════
// REMOVE geometric constraint
// ═══════════════════════════════════════════════════════════════════════

export interface RemoveGeometricConstraintInput {
  document: DrawingDocument;
  blockName: string;
  constraintId: string;
}

export function removeGeometricConstraintMutation(
  input: RemoveGeometricConstraintInput,
): GeometricConstraintMutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const existing = (meta.geometricConstraints ?? []).find(
    (c) => c.id === input.constraintId,
  );
  if (!existing) {
    throw new Error(`Geometric constraint '${input.constraintId}' not found`);
  }
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    geometricConstraints: (meta.geometricConstraints ?? []).filter(
      (c) => c.id !== input.constraintId,
    ),
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Removed ${existing.kind} geometric constraint '${input.constraintId}' from block '${input.blockName}'`,
  );
}
